/* eslint-disable @typescript-eslint/member-ordering */
import { GlossaryHelperTypes } from './types'
import {
  generatePlaceholder,
  createTermRegex,
  detectTermConflicts,
  normalizeTermText,
  findAllPlaceholders,
  parsePlaceholder,
  resolveTermConflicts,
} from './utils'

/**
 * 术语表处理器核心类
 */
export class GlossaryProcessor {
  private config: GlossaryHelperTypes.ProcessorConfig
  private regexCache: Map<string, GlossaryHelperTypes.RegexCacheItem> = new Map()
  private performanceStats: GlossaryHelperTypes.PerformanceStats = {
    cacheHits: 0,
    cacheMisses: 0,
    cacheHitRate: 0,
    averageProcessingTime: 0,
    totalProcessingCount: 0,
  }

  constructor(config: GlossaryHelperTypes.ProcessorConfig = {}) {
    this.config = {
      placeholderPrefix: 't',
      matchConfig: {
        caseSensitive: true,
        wholeWord: true,
      },
      debug: process.env.REACT_APP_ENV === 'local',
      performance: {
        regexCacheSize: 100,
        shortTextThreshold: 100,
        enablePerformanceMonitoring: false,
        enableCacheStats: false,
        ...config.performance,
      },
      ...config,
    }
  }

  /**
   * 获取缓存的正则表达式
   * @param term 术语文本
   * @param options 匹配选项
   * @returns 正则表达式对象
   */
  private getCachedRegex(term: string, options: GlossaryHelperTypes.MatchConfig = {}): RegExp {
    const cacheKey = `${term}_${JSON.stringify(options)}`
    const cached = this.regexCache.get(cacheKey)

    if (cached) {
      // 更新缓存统计
      cached.lastUsed = Date.now()
      cached.useCount++
      this.performanceStats.cacheHits++
      this.updateCacheHitRate()

      if (this.config.debug) {
        console.log(`正则表达式缓存命中: ${cacheKey}`)
      }
      return cached.regex
    }

    // 缓存未命中，创建新的正则表达式
    this.performanceStats.cacheMisses++
    this.updateCacheHitRate()

    const regex = createTermRegex(term, options)
    const cacheItem: GlossaryHelperTypes.RegexCacheItem = {
      regex,
      lastUsed: Date.now(),
      useCount: 1,
    }

    // 检查缓存大小限制
    if (this.regexCache.size >= (this.config.performance?.regexCacheSize || 100)) {
      this.evictLeastUsedCache()
    }

    this.regexCache.set(cacheKey, cacheItem)

    if (this.config.debug) {
      console.log(`正则表达式缓存未命中，创建新缓存: ${cacheKey}`)
    }

    return regex
  }

  /**
   * 更新缓存命中率
   */
  private updateCacheHitRate(): void {
    const total = this.performanceStats.cacheHits + this.performanceStats.cacheMisses
    this.performanceStats.cacheHitRate = total > 0 ? this.performanceStats.cacheHits / total : 0
  }

  /**
   * 清理最少使用的缓存项
   */
  private evictLeastUsedCache(): void {
    let leastUsedKey: string | null = null
    let leastUsedTime = Infinity

    for (const [key, item] of this.regexCache.entries()) {
      if (item.lastUsed < leastUsedTime) {
        leastUsedTime = item.lastUsed
        leastUsedKey = key
      }
    }

    if (leastUsedKey) {
      this.regexCache.delete(leastUsedKey)
      if (this.config.debug) {
        console.log(`清理缓存项: ${leastUsedKey}`)
      }
    }
  }

  /**
   * 获取性能统计信息
   * @returns 性能统计信息
   */
  public getPerformanceStats(): GlossaryHelperTypes.PerformanceStats {
    return { ...this.performanceStats }
  }

  /**
   * 清理缓存
   */
  public clearCache(): void {
    this.regexCache.clear()
    if (this.config.debug) {
      console.log('清理所有正则表达式缓存')
    }
  }

  /**
   * 文本预处理：将术语替换为占位符
   * @param text 原始文本
   * @param glossaryList 术语表列表
   * @returns 预处理结果
   */
  // eslint-disable-next-line complexity
  public preprocessText(
    text: string,
    glossaryList: GlossaryHelperTypes.GlossaryData[]
  ): GlossaryHelperTypes.PreprocessResult {
    const startTime = this.config.performance?.enablePerformanceMonitoring ? Date.now() : 0

    if (!glossaryList || glossaryList.length === 0) {
      return {
        processedText: text,
        placeholders: [],
        hasReplacements: false,
      }
    }

    // 智能处理策略：对短文本使用简化处理
    const shortTextThreshold = this.config.performance?.shortTextThreshold || 100
    if (text.length <= shortTextThreshold) {
      return this.preprocessShortText(text, glossaryList)
    }

    // 处理术语列表
    const terms: GlossaryHelperTypes.GlossaryData[] = [...glossaryList]

    // 检测术语冲突（仅在调试模式下进行，避免性能开销）
    if (this.config.debug) {
      const conflictResult = detectTermConflicts(terms, text)
      if (conflictResult.hasConflicts) {
        console.warn('检测到术语冲突:', conflictResult.conflicts)
      }
    }

    // 批量收集所有替换位置
    const allReplacements: Array<{
      start: number
      end: number
      placeholder: string
      originalText: string
      targetTerm: string
      termIndex: number
      termId: number
    }> = []

    const maxReplacements = this.config.matchConfig?.maxReplacements || Infinity
    let totalReplacements = 0

    // 使用缓存的正则表达式进行匹配
    for (let i = 0; i < terms.length && totalReplacements < maxReplacements; i++) {
      const term = terms[i]
      const normalizedFrom = normalizeTermText(term.fromText)

      // 使用缓存的正则表达式
      const regex = this.getCachedRegex(normalizedFrom, {
        ...this.config.matchConfig,
      })

      const placeholder = generatePlaceholder(term.termId, this.config.placeholderPrefix)
      regex.lastIndex = 0

      let match
      while ((match = regex.exec(text)) !== null && totalReplacements < maxReplacements) {
        const start = match.index
        const end = start + match[0].length
        const originalText = match[0]

        allReplacements.push({
          start,
          end,
          placeholder,
          originalText,
          targetTerm: term.toText,
          termIndex: i,
          termId: term.termId,
        })

        totalReplacements++

        // 防止无限循环
        if (match[0].length === 0) {
          regex.lastIndex++
        }
      }
    }

    // 检查替换项数量是否超过阈值，超过则直接跳过术语处理
    const conflictDetectionThreshold = this.config.matchConfig?.conflictDetectionThreshold ?? 1000
    if (allReplacements.length > conflictDetectionThreshold) {
      if (this.config.debug) {
        console.warn(
          `跳过术语处理：替换项数量 ${allReplacements.length} 超过阈值 ${conflictDetectionThreshold}，为避免性能问题和冲突风险，已跳过所有术语处理`
        )
      }
      return {
        processedText: text,
        placeholders: [],
        hasReplacements: false,
      }
    }

    // 解决术语冲突
    const conflictResolution = this.config.matchConfig?.conflictResolution || 'longest'
    const startConflictTime = this.config.performance?.enablePerformanceMonitoring ? Date.now() : 0

    const conflictResolutionResult = resolveTermConflicts(allReplacements, conflictResolution)
    const resolvedReplacements = conflictResolutionResult.filteredReplacements

    // 性能监控
    if (this.config.performance?.enablePerformanceMonitoring && startConflictTime > 0) {
      const conflictTime = Date.now() - startConflictTime
      if (this.config.debug) {
        console.log(`冲突检测耗时: ${conflictTime}ms, 替换项数量: ${allReplacements.length}`)
      }
    }

    // 在调试模式下输出冲突解决信息
    if (this.config.debug && conflictResolutionResult.resolvedConflicts.length > 0) {
      console.warn('术语冲突已解决:', {
        策略: conflictResolution,
        冲突数量: conflictResolutionResult.resolvedConflicts.length,
        解决详情: conflictResolutionResult.resolvedConflicts,
      })
    }

    // 按位置排序，从后往前处理
    resolvedReplacements.sort((a, b) => b.start - a.start)

    // 批量替换
    let processedText = text
    const placeholders: GlossaryHelperTypes.PlaceholderInfo[] = []

    for (const replacement of resolvedReplacements) {
      const { start, end, placeholder, originalText, targetTerm, termIndex, termId } = replacement

      // 记录占位符信息
      placeholders.push({
        placeholder,
        originalTerm: originalText,
        targetTerm,
        position: { start, end },
        termIndex,
        termId,
      })

      // 替换文本
      processedText = processedText.substring(0, start) + placeholder + processedText.substring(end)

      if (this.config.debug) {
        console.log(`替换术语: "${originalText}" -> "${placeholder}"`)
      }
    }

    // 更新性能统计
    if (this.config.performance?.enablePerformanceMonitoring && startTime > 0) {
      const processingTime = Date.now() - startTime
      this.performanceStats.totalProcessingCount++
      this.performanceStats.averageProcessingTime =
        (this.performanceStats.averageProcessingTime *
          (this.performanceStats.totalProcessingCount - 1) +
          processingTime) /
        this.performanceStats.totalProcessingCount
    }

    return {
      processedText,
      placeholders,
      hasReplacements: placeholders.length > 0,
    }
  }

  /**
   * 短文本预处理（简化版本）
   * @param text 原始文本
   * @param glossaryList 术语表列表
   * @returns 预处理结果
   */
  private preprocessShortText(
    text: string,
    glossaryList: GlossaryHelperTypes.GlossaryData[]
  ): GlossaryHelperTypes.PreprocessResult {
    // 对于短文本，也收集所有替换位置，然后应用冲突解决
    const allReplacements: Array<{
      start: number
      end: number
      placeholder: string
      originalText: string
      targetTerm: string
      termIndex: number
      termId: number
    }> = []

    // 收集所有匹配位置
    for (let i = 0; i < glossaryList.length; i++) {
      const term = glossaryList[i]
      const normalizedFrom = normalizeTermText(term.fromText)
      const placeholder = generatePlaceholder(term.termId, this.config.placeholderPrefix)

      // 使用正则表达式查找所有匹配位置（包括重叠的）
      const regex = this.getCachedRegex(normalizedFrom, {
        ...this.config.matchConfig,
      })
      regex.lastIndex = 0

      let match
      while ((match = regex.exec(text)) !== null) {
        const start = match.index
        const end = start + match[0].length
        const originalText = match[0]

        allReplacements.push({
          start,
          end,
          placeholder,
          originalText,
          targetTerm: term.toText,
          termIndex: i,
          termId: term.termId,
        })

        // 防止无限循环
        if (match[0].length === 0) {
          regex.lastIndex++
        }
      }
    }

    // 检查替换项数量是否超过阈值，超过则直接跳过术语处理
    const conflictDetectionThreshold = this.config.matchConfig?.conflictDetectionThreshold ?? 1000
    if (allReplacements.length > conflictDetectionThreshold) {
      if (this.config.debug) {
        console.warn(
          `跳过术语处理：替换项数量 ${allReplacements.length} 超过阈值 ${conflictDetectionThreshold}，为避免性能问题和冲突风险，已跳过所有术语处理`
        )
      }
      return {
        processedText: text,
        placeholders: [],
        hasReplacements: false,
      }
    }

    // 解决术语冲突
    const conflictResolution = this.config.matchConfig?.conflictResolution || 'longest'
    const conflictResolutionResult = resolveTermConflicts(allReplacements, conflictResolution)
    const resolvedReplacements = conflictResolutionResult.filteredReplacements

    // 在调试模式下输出冲突解决信息
    if (this.config.debug && conflictResolutionResult.resolvedConflicts.length > 0) {
      console.warn('短文本术语冲突已解决:', {
        策略: conflictResolution,
        冲突数量: conflictResolutionResult.resolvedConflicts.length,
        解决详情: conflictResolutionResult.resolvedConflicts,
      })
    }

    // 按位置排序，从后往前处理
    resolvedReplacements.sort((a, b) => b.start - a.start)

    // 批量替换
    let processedText = text
    const placeholders: GlossaryHelperTypes.PlaceholderInfo[] = []

    for (const replacement of resolvedReplacements) {
      const { start, end, placeholder, originalText, targetTerm, termIndex, termId } = replacement

      placeholders.push({
        placeholder,
        originalTerm: originalText,
        targetTerm,
        position: { start, end },
        termIndex,
        termId,
      })

      processedText = processedText.substring(0, start) + placeholder + processedText.substring(end)

      if (this.config.debug) {
        console.log(`替换术语: "${originalText}" -> "${placeholder}"`)
      }
    }

    return {
      processedText,
      placeholders,
      hasReplacements: placeholders.length > 0,
    }
  }

  /**
   * 文本后处理：将占位符还原为目标术语
   * @param translatedText 翻译后的文本（包含占位符）
   * @param preprocessResult 预处理结果
   * @returns 后处理结果
   */
  public postprocessText(
    translatedText: string,
    preprocessResult: GlossaryHelperTypes.PreprocessResult
  ): GlossaryHelperTypes.PostprocessResult {
    const startTime = this.config.performance?.enablePerformanceMonitoring ? Date.now() : 0

    if (!preprocessResult.hasReplacements) {
      return {
        finalText: translatedText,
        recoveredCount: 0,
        errors: [],
      }
    }

    const errors: string[] = []
    let finalText = translatedText
    let recoveredCount = 0

    // 创建占位符索引映射，优化查找性能
    const placeholderMap = new Map<number, GlossaryHelperTypes.PlaceholderInfo>()
    preprocessResult.placeholders.forEach((placeholderInfo) => {
      placeholderMap.set(placeholderInfo.termId, placeholderInfo)
    })

    // 查找翻译文本中的所有占位符
    const foundPlaceholders = findAllPlaceholders(translatedText, this.config.placeholderPrefix)

    // 批量替换占位符
    const replacements: Array<{ placeholder: string; targetTerm: string; termId: number }> = []

    for (const { placeholder, termId } of foundPlaceholders) {
      try {
        // 使用Map进行快速查找
        const placeholderInfo = placeholderMap.get(termId)

        if (placeholderInfo) {
          replacements.push({
            placeholder,
            targetTerm: placeholderInfo.targetTerm,
            termId,
          })
          recoveredCount++

          if (this.config.debug) {
            console.log(`恢复占位符: "${placeholder}" -> "${placeholderInfo.targetTerm}"`)
          }
        } else {
          errors.push(`未找到术语ID为 ${termId} 的术语信息`)
        }
      } catch (error) {
        errors.push(`处理占位符 "${placeholder}" 时发生错误: ${error}`)
      }
    }

    // 批量替换，避免多次字符串操作
    for (const { placeholder, targetTerm } of replacements) {
      finalText = finalText.replace(placeholder, targetTerm)
    }

    // 处理任何遗漏的占位符（仅在调试模式下进行）
    if (this.config.debug) {
      const regexPattern = `<${this.config.placeholderPrefix}(\\d+)></${this.config.placeholderPrefix}\\1>`
      const remainingPlaceholderRegex = new RegExp(regexPattern, 'g')
      finalText = finalText.replace(remainingPlaceholderRegex, (match) => {
        const termId = parsePlaceholder(match, this.config.placeholderPrefix)
        if (termId !== null) {
          const placeholderInfo = placeholderMap.get(termId)
          if (placeholderInfo) {
            recoveredCount++
            return placeholderInfo.targetTerm
          }
        }
        errors.push(`无法处理的占位符: ${match}`)
        return match // 保留原始占位符
      })
    }

    // 更新性能统计
    if (this.config.performance?.enablePerformanceMonitoring && startTime > 0) {
      const processingTime = Date.now() - startTime
      this.performanceStats.totalProcessingCount++
      this.performanceStats.averageProcessingTime =
        (this.performanceStats.averageProcessingTime *
          (this.performanceStats.totalProcessingCount - 1) +
          processingTime) /
        this.performanceStats.totalProcessingCount
    }

    return {
      finalText,
      recoveredCount,
      errors,
    }
  }

  /**
   * 更新处理器配置
   * @param newConfig 新的配置选项
   */
  public updateConfig(newConfig: Partial<GlossaryHelperTypes.ProcessorConfig>): void {
    this.config = {
      ...this.config,
      ...newConfig,
      matchConfig: {
        ...this.config.matchConfig,
        ...newConfig.matchConfig,
      },
      performance: {
        ...this.config.performance,
        ...newConfig.performance,
      },
    }
  }

  /**
   * 获取当前配置
   * @returns 当前配置对象的副本
   */
  public getConfig(): GlossaryHelperTypes.ProcessorConfig {
    return { ...this.config }
  }
}

/**
 * 创建默认的术语表处理器实例
 * @param config 可选的配置参数
 * @returns 术语表处理器实例
 */
export function createGlossaryProcessor(
  config?: GlossaryHelperTypes.ProcessorConfig
): GlossaryProcessor {
  return new GlossaryProcessor(config)
}
